import { VariantProps } from 'class-variance-authority';
import React, { useCallback, useMemo } from 'react';

import {
  CommandSeparator,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Command,
} from '../command/command';
import { CheckboxUnchecked, CheckboxChecked, Cancel } from '../icons';
import { typographyVariants } from '../typography/typography';
import { Button } from '../button/button';
import { cn } from '../../utils';

interface TreeViewSelectContextValue {
  currentPath: string;
  setCurrentPath: (path: string) => void;
  selected: string[];
  onSelect: (selected: string[]) => void;
  isSelected: (item: undefined | string) => boolean;
}

interface TreeViewSelectNodeContextValue {
  parentPath: string;
}

const TreeViewSelectContext = React.createContext<TreeViewSelectContextValue>({
  selected: [],
  isSelected: () => false,
  onSelect: () => {},
  currentPath: '',
  setCurrentPath: () => {},
});
const TreeNodeContext = React.createContext<TreeViewSelectNodeContextValue>({ parentPath: '' });

export interface TreeViewSelectProps extends React.ComponentPropsWithoutRef<typeof Command> {
  selected?: string[];
  onSelectedChange?: (selected: string[]) => void;
  path?: string;
  onPathChange?: (path: string) => void;
}

const filterFn = (value: string, search: string) =>
  value.toLowerCase().startsWith(search.toLowerCase()) ? 1 : -1;

export const TreeViewSelect: React.FC<TreeViewSelectProps> = ({
  children,
  path,
  onPathChange = () => {},
  selected: value,
  onSelectedChange = () => {},
  filter = filterFn,
  ...props
}) => {
  const [currentPath, setCurrentPath] = React.useState<string>(path || '');
  const [selected, setSelected] = React.useState<string[]>(value || []);

  React.useEffect(() => {
    if (value && JSON.stringify(value) !== JSON.stringify(selected)) {
      setSelected(value);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, setSelected]);

  React.useEffect(() => {
    if (path && path !== currentPath) {
      setCurrentPath(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, setCurrentPath]);

  const onSelect = useCallback(
    (items: string[]) => {
      setSelected(items);
      onSelectedChange(items);
    },
    [setSelected, onSelectedChange],
  );

  const onCurrentPathChange = useCallback(
    (newPath: string) => {
      setCurrentPath(newPath);
      onPathChange(newPath);
    },
    [setCurrentPath, onPathChange],
  );

  const isSelected = useCallback(
    (item: undefined | string) => item !== undefined && selected.includes(item),
    [selected],
  );

  return (
    <TreeViewSelectContext.Provider
      value={{
        selected,
        onSelect,
        currentPath,
        setCurrentPath: onCurrentPathChange,
        isSelected,
      }}
    >
      <Command className="bg-transparent rounded-none" filter={filter} {...props}>
        {children}
      </Command>
    </TreeViewSelectContext.Provider>
  );
};
TreeViewSelect.displayName = 'TreeViewSelect';

export const TreeViewSelectInput: React.FC<
  React.ComponentPropsWithoutRef<typeof CommandInput> & { avaibleForPaths?: string[] }
> = ({ children, className, avaibleForPaths = [''], ...props }) => {
  const { currentPath } = React.useContext(TreeViewSelectContext);
  const hide = useMemo(
    () => avaibleForPaths && !avaibleForPaths.includes(currentPath),
    [avaibleForPaths, currentPath],
  );

  if (hide) return null;

  return (
    <div className="sticky -top-0.25 bg-background z-10">
      <CommandInput
        className={cn('ml-2 bg-transparent outline-none placeholder:text-weak flex-1', className)}
        {...props}
      >
        {children}
      </CommandInput>
    </div>
  );
};
TreeViewSelectInput.displayName = 'TreeViewSelectInput';

export const TreeViewSelectSeparator = CommandSeparator;
TreeViewSelectSeparator.displayName = 'TreeViewSelectSeparator';

export const TreeViewSelectList: React.FC<React.ComponentPropsWithoutRef<typeof CommandList>> = ({
  children,
  className,
  ...props
}) => {
  return (
    <CommandList className={cn('rounded-none relative max-h-138', className)} {...props}>
      {children}
    </CommandList>
  );
};
TreeViewSelectList.displayName = 'TreeViewSelectList';

export const TreeViewSelectGroup: React.FC<React.ComponentPropsWithoutRef<typeof CommandGroup>> = ({
  children,
  className,
  ...props
}) => (
  <CommandGroup
    className={cn(typographyVariants({ variant: 'labelMediumMedium' }), className)}
    {...props}
  >
    {children}
  </CommandGroup>
);
TreeViewSelectGroup.displayName = 'TreeViewSelectGroup';

export const TreeViewSelectNode: React.FC<
  {
    label: React.ReactNode | string;
    values?: string[];
    path: string;
  } & Omit<React.ComponentPropsWithoutRef<typeof CommandItem>, 'value'>
> = ({ children, label, values, className, ...props }) => {
  const { currentPath, setCurrentPath, selected } = React.useContext(TreeViewSelectContext);
  const { parentPath } = React.useContext(TreeNodeContext);
  const path = React.useMemo(() => `${parentPath}/${props.path}`, [parentPath, props.path]);

  const selecedFromNode = React.useMemo(
    () => selected.filter((val) => values?.includes(val)),
    [selected, values],
  );

  const selectedAll = useMemo(
    () => selecedFromNode.length === values?.length,
    [selecedFromNode, values],
  );

  return (
    <>
      {currentPath === parentPath && (
        <CommandItem
          className={cn(
            'px-2 py-3',
            typographyVariants({ variant: 'labelMediumBook' }),
            {
              'group/tree-view-select-node-selected': selectedAll,
            },
            className,
          )}
          onSelect={() => setCurrentPath(path)}
          {...props}
        >
          {label}
        </CommandItem>
      )}
      {currentPath === path && <>{children}</>}
    </>
  );
};
TreeViewSelectNode.displayName = 'TreeViewSelectNode';

const DummyCheckbox = ({ checked }: { checked: boolean }) => {
  return checked ? (
    <CheckboxChecked className="size-4 text-surface" />
  ) : (
    <CheckboxUnchecked className="size-4 text-border-default" />
  );
};

export const TreeViewSelectItem: React.FC<React.ComponentPropsWithoutRef<typeof CommandItem>> = ({
  children,
  className,
  ...props
}) => {
  const { selected, onSelect, isSelected } = React.useContext(TreeViewSelectContext);

  const select = useCallback(
    (item: undefined | string) => {
      if (!item) return;
      if (isSelected(item)) return onSelect(selected.filter((val) => val !== item));
      onSelect([...selected, item]);
    },
    [selected, onSelect, isSelected],
  );

  return (
    <CommandItem
      className={cn('px-2 py-3.5', typographyVariants({ variant: 'labelMediumBook' }), className, {
        'tree-view-select-item-selected': props.value && isSelected(props.value),
      })}
      onSelect={() => props.value && select(props.value)}
      {...props}
    >
      <DummyCheckbox checked={isSelected(props.value)} /> {children}
    </CommandItem>
  );
};
TreeViewSelectItem.displayName = 'TreeViewSelectItem';

export const TreeViewSelectBackButton: React.FC<React.ComponentPropsWithoutRef<typeof Button>> = (
  props,
) => {
  const { setCurrentPath } = React.useContext(TreeViewSelectContext);
  const { parentPath } = React.useContext(TreeNodeContext);

  return (
    <Button
      onClick={() => setCurrentPath(parentPath)}
      className="text-weak"
      variant="link"
      icon
      {...props}
    >
      <Cancel />
    </Button>
  );
};
TreeViewSelectBackButton.displayName = 'TreeViewSelectBackButton';

export const TreeViewSelectNodeHeader: React.FC<
  VariantProps<typeof typographyVariants> & React.HTMLAttributes<HTMLDivElement>
> = ({ children, className, variant, ...props }) => (
  <div
    className={cn(
      'flex items-center justify-between border-b-1 border-border-weak sticky -top-0.25 bg-background z-10',
      typographyVariants({ variant }),
      className,
    )}
    {...props}
  >
    <div className="p-3">{children}</div>
    <TreeViewSelectBackButton />
  </div>
);

TreeViewSelectNodeHeader.displayName = 'TreeViewSelectNodeHeader';
